import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, Platform } from 'react-native';
import { CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, RefreshCw } from 'lucide-react-native';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/hooks/useAuth';
import { darkTheme } from '@/hooks/useTheme';
import { supabaseUrl } from '@/lib/supabase';
import { persistentCountryCache } from '@/services/persistentCountryCacheService';

type DCBStep = 'activating' | 'success' | 'error';

interface DCBPaymentFlowProps {
  operationId: string;
  isNewUser: boolean;
  identifier: string;
  identityType: 'EMAIL' | 'MSISDN';
  sessionToken: string;
  smartuserId: string;
  supabaseUserId: string;
  onComplete: () => void;
  onError?: (error: string) => void;
  dcbSessionToken?: string;
  dcbAutologinToken?: string;
  dcbTemporaryAbsoluteReference?: string;
  dcbIdentities?: Array<{ type: string; identifier: string }>;
}

const PAYMENT_PROVIDER = 'DVP4M';

async function callManageUsers(action: string, body: Record<string, unknown>): Promise<any> {
  const response = await fetch(`${supabaseUrl}/functions/v1/manage-users`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ action, ...body }),
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.error || data.message || `Action ${action} failed`);
  }

  return data;
}

export function DCBPaymentFlow({
  operationId,
  isNewUser,
  identifier,
  identityType,
  sessionToken,
  smartuserId,
  supabaseUserId,
  onComplete,
  onError,
  dcbSessionToken: preFetchedDcbSessionToken,
  dcbAutologinToken: preFetchedDcbAutologinToken,
  dcbTemporaryAbsoluteReference: preFetchedDcbTemporaryAbsoluteReference,
  dcbIdentities,
}: DCBPaymentFlowProps) {
  const [step, setStep] = useState<DCBStep>('activating');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();
  const { updateUserSubscription, updateUserPaymentMethod } = useAuth();
  const theme = darkTheme;
  const isProcessingRef = useRef(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const activateSubscription = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      if (!isMountedRef.current) return;
      setStep('activating');
      setErrorMessage('');

      const countryData = await persistentCountryCache.get();
      const countryCode = (countryData?.countryCode || 'FR').toUpperCase();

      let dcbSessionToken: string;
      let dcbAutologinToken: string | undefined;
      let dcbTemporaryAbsoluteReference: string;

      if (preFetchedDcbSessionToken && preFetchedDcbTemporaryAbsoluteReference) {
        console.log('[DCBPaymentFlow] Using pre-fetched DCB tokens (no extra privileged_login call)');
        dcbSessionToken = preFetchedDcbSessionToken;
        dcbAutologinToken = preFetchedDcbAutologinToken;
        dcbTemporaryAbsoluteReference = preFetchedDcbTemporaryAbsoluteReference;
      } else {
        const privilegedLoginResult = await callManageUsers('dcb_privileged_login', {
          operationId,
          paymentProvider: PAYMENT_PROVIDER,
        });

        dcbSessionToken = privilegedLoginResult.sessionToken;
        dcbAutologinToken = privilegedLoginResult.autologinToken;
        dcbTemporaryAbsoluteReference = privilegedLoginResult.temporaryAbsoluteReference;
      }

      if (!dcbSessionToken || !dcbTemporaryAbsoluteReference) {
        throw new Error(t('otp.dcbPaymentNotFound'));
      }

      if (isNewUser) {
        await callManageUsers('dcb_create_user', {
          operationId,
          paymentProvider: PAYMENT_PROVIDER,
          paymentSessionToken: dcbSessionToken,
          autologinToken: dcbAutologinToken,
          temporaryAbsoluteReference: dcbTemporaryAbsoluteReference,
          identifier,
          identityType,
          sessionToken,
          smartuserId,
          supabaseUserId,
          suggestedCountry: countryCode,
          dcbIdentities: dcbIdentities && dcbIdentities.length > 0 ? JSON.stringify(dcbIdentities) : undefined,
        });
      } else {
        const mergeResult = await callManageUsers('dcb_merge_request', {
          operationId,
          paymentProvider: PAYMENT_PROVIDER,
          paymentSessionToken: dcbSessionToken,
          paymentAutologinToken: dcbAutologinToken,
          temporaryAbsoluteReference: dcbTemporaryAbsoluteReference,
          identifier,
          identityType,
          sessionToken,
          smartuserId,
          dcbIdentities: dcbIdentities && dcbIdentities.length > 0 ? JSON.stringify(dcbIdentities) : undefined,
        });

        if (!mergeResult.skippedMerge) {
          await callManageUsers('dcb_merge_finalize', {
            mergeToken: mergeResult.mergeToken,
            sessionToken,
            smartuserId,
            supabaseUserId,
          });
        } else {
          console.log('[DCBPaymentFlow] Merge skipped (same MSISDN), subscription activated directly');
        }
      }

      updateUserSubscription(true);
      updateUserPaymentMethod('dcb');

      if (Platform.OS === 'web') {
        try {
          localStorage.removeItem('dcb_last_operation_id');
          localStorage.removeItem('dcb_operation_id_stored_at');
          console.log('[DCBPaymentFlow] Cleared DCB localStorage entries after successful activation');
        } catch {}
      }

      if (isMountedRef.current) {
        setStep('success');
      }
    } catch (error: any) {
      console.error('[DCBPaymentFlow] Activation error:', error);
      if (isMountedRef.current) {
        const msg = error?.message || t('otp.dcbErrorRetry');
        setErrorMessage(msg);
        setStep('error');
        onError?.(msg);
      }
    } finally {
      isProcessingRef.current = false;
    }
  }, [operationId, isNewUser, identifier, identityType, sessionToken, smartuserId, supabaseUserId, preFetchedDcbSessionToken, preFetchedDcbTemporaryAbsoluteReference, dcbIdentities]);

  useEffect(() => {
    activateSubscription();
  }, [activateSubscription]);

  const handleRetry = () => {
    activateSubscription();
  };

  const handleStartWatching = () => {
    onComplete();
  };

  if (step === 'activating') {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.activatingText}>{t('otp.dcbActivating')}</Text>
      </View>
    );
  }

  if (step === 'success') {
    return (
      <View style={styles.container}>
        <View style={styles.iconCircle}>
          <CheckCircle size={48} color="#22C55E" />
        </View>
        <Text style={styles.successTitle}>{t('otp.dcbSuccessTitle')}</Text>
        <Text style={styles.successMessage}>{t('otp.dcbSuccessMessage')}</Text>
        <TouchableOpacity style={styles.primaryButton} onPress={handleStartWatching}>
          <Text style={styles.primaryButtonText}>{t('otp.dcbStartWatching')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.errorIconCircle}>
        <AlertTriangle size={48} color="#EF4444" />
      </View>
      <Text style={styles.errorTitle}>{t('otp.dcbErrorTitle')}</Text>
      <Text style={styles.errorMessage}>{errorMessage || t('otp.dcbErrorRetry')}</Text>
      <TouchableOpacity style={styles.retryButton} onPress={handleRetry}>
        <RefreshCw size={20} color="#ffffff" />
        <Text style={styles.retryButtonText}>{t('otp.dcbRetryButton')}</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingVertical: 48,
    backgroundColor: darkTheme.colors.cardBackground,
  },
  activatingText: {
    marginTop: 24,
    fontSize: 17,
    fontWeight: '500',
    color: darkTheme.colors.textSecondary,
    textAlign: 'center',
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: darkTheme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 12,
  },
  successMessage: {
    fontSize: 16,
    color: darkTheme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  primaryButton: {
    backgroundColor: darkTheme.colors.primary,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 32,
    alignItems: 'center',
    width: '100%',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
  },
  errorIconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: darkTheme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: 12,
  },
  errorMessage: {
    fontSize: 16,
    color: darkTheme.colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 16,
  },
  retryButton: {
    backgroundColor: darkTheme.colors.primary,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
  },
});

export default DCBPaymentFlow;
